import { useState, type ReactNode } from "react";
import { EfLogo } from "@/components/Logo";
import { IcoCpu, IcoEye, IcoSparkle, IcoSplit, IcoWand } from "@/components/icons";

const FEATURES = [
  { icon: <IcoSplit size={16} />, title: "Non-linear editing", desc: "Multi-track timeline, frame-precise cuts, ripple & slip" },
  { icon: <IcoSparkle size={16} />, title: "AI automation", desc: "Auto-transcribe, silence removal, color match, B-roll" },
  { icon: <IcoWand size={16} />, title: "Director mode", desc: "Natural language editing — describe, AI executes" },
  { icon: <IcoCpu size={16} />, title: "Self-hosted", desc: "Your data stays on your server, no vendor lock-in" },
];

// Social-proof cards. The design names sibling products here; the standalone-
// SaaS principle (CLAUDE.md) forbids hardcoding sibling-product names, so we
// keep the card structure with product-neutral proof points.
const PROOF = [
  { mark: "10×", name: "Faster edits", desc: "AI cuts the busywork" },
  { mark: "100%", name: "Self-hosted", desc: "Your data, your server" },
  { mark: "3", name: "Edit modes", desc: "Manual · Half · Director" },
];

// Left brand panel, shown only on large (>=1440px) screens.
function BrandPanel() {
  return (
    <aside
      className="relative hidden w-[44%] shrink-0 flex-col justify-between overflow-hidden border-r border-ef-border p-[52px] min-[1440px]:flex"
      style={{ background: "linear-gradient(160deg, #0d1420 0%, #0B0D10 60%)" }}
    >
      {/* Ambient glows + faint grid */}
      <div
        className="pointer-events-none absolute left-[-10%] top-[-10%] h-1/2 w-1/2"
        style={{ background: "radial-gradient(ellipse, rgba(34,197,176,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="pointer-events-none absolute bottom-[-10%] right-[-10%] h-2/5 w-2/5"
        style={{ background: "radial-gradient(ellipse, rgba(168,114,241,0.08) 0%, transparent 70%)" }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.02) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.02) 1px,transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="relative">
        <EfLogo size="lg" to={null} />
        <p className="mt-4 max-w-sm text-[13px] leading-relaxed text-ef-textMuted">
          Web-based NLE with AI automation.<br />
          CapCut + Descript + Runway — self-hosted.
        </p>
      </div>
      <ul className="relative flex flex-col gap-[22px]">
        {FEATURES.map((f) => (
          <li key={f.title} className="flex gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius)] border border-ef-accent/15 bg-ef-accent/10 text-ef-accent">
              {f.icon}
            </span>
            <div>
              <div className="text-sm font-semibold text-ef-text">{f.title}</div>
              <div className="text-xs leading-relaxed text-ef-textMuted">{f.desc}</div>
            </div>
          </li>
        ))}
      </ul>
      <div className="relative">
        <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.1em] text-ef-textMuted">Built for serious creators</div>
        <div className="flex gap-2">
          {PROOF.map((p) => (
            <div key={p.name} className="flex flex-1 items-center gap-2 rounded-[var(--radius)] border border-ef-border bg-ef-panel/40 px-2.5 py-2">
              {/* 9px text inside a 22px circle was below legibility; match the
                  FEATURES icon size (h-8) so the mark can hold a readable 11px. */}
              <span className="ef-gradient flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white">
                {p.mark}
              </span>
              <div className="min-w-0">
                <div className="truncate text-[11px] font-semibold text-ef-text">{p.name}</div>
                <div className="truncate text-[10px] text-ef-textMuted">{p.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}

// Auth scaffold: split-screen on large screens (brand + form), centered card otherwise.
export function AuthShell({ subtitle, children }: { subtitle: string; children: ReactNode }) {
  return (
    <div className="flex min-h-dvh bg-ef-bg">
      <BrandPanel />
      <div className="relative flex flex-1 items-center justify-center overflow-hidden p-5">
        {/* Ambient glows */}
        <div
          className="pointer-events-none absolute left-1/2 top-[-20%] h-[400px] w-[600px] -translate-x-1/2"
          style={{ background: "radial-gradient(ellipse, rgba(34,197,176,0.08) 0%, transparent 70%)" }}
        />
        <div
          className="pointer-events-none absolute bottom-[-10%] right-[20%] h-[300px] w-[400px]"
          style={{ background: "radial-gradient(ellipse, rgba(168,114,241,0.06) 0%, transparent 70%)" }}
        />
        <div
          className="pointer-events-none absolute inset-0 min-[1440px]:hidden"
          style={{
            backgroundImage:
              "linear-gradient(rgba(38,44,52,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(38,44,52,0.4) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
            maskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)",
            WebkitMaskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)",
          }}
        />

        <div className="relative z-10 w-full max-w-[420px]">
          <div className="relative overflow-hidden rounded-2xl border border-ef-border bg-ef-panel px-9 pb-8 pt-9 shadow-[0_24px_64px_rgba(0,0,0,0.5)]">
            {/* Gradient top hairline */}
            <div
              className="absolute inset-x-0 top-0 h-px"
              style={{ background: "linear-gradient(90deg, transparent, rgba(34,197,176,0.4), transparent)" }}
            />
            <div className="mb-7 flex flex-col items-center gap-3">
              <EfLogo size="lg" to={null} />
              <p className="text-center text-[13px] leading-relaxed text-ef-textDim">{subtitle}</p>
            </div>
            {children}
          </div>
          <p className="mt-5 text-center text-[11px] text-ef-textMuted">
            EditsForge · SoClose Society · <span className="text-ef-textDim">v0.1.0-alpha</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export function AuthDivider() {
  return (
    <div className="mb-5 flex items-center gap-3">
      <div className="h-px flex-1 bg-ef-border" />
      <span className="whitespace-nowrap text-[11px] font-semibold tracking-[0.08em] text-ef-textMuted">OR EMAIL</span>
      <div className="h-px flex-1 bg-ef-border" />
    </div>
  );
}

export function AuthSubmit({ loading, disabled, children }: { loading: boolean; disabled?: boolean; children: ReactNode }) {
  return (
    <button
      type="submit"
      disabled={loading || disabled}
      className="ef-gradient mt-1 w-full rounded-[var(--radius)] py-[11px] text-sm font-semibold text-white shadow-[0_4px_16px_rgba(34,197,176,0.3)] transition-opacity disabled:cursor-not-allowed disabled:opacity-60"
    >
      {children}
    </button>
  );
}

// Password input with a show/hide eye toggle. `label` optional.
export function PasswordField({
  label,
  value,
  onChange,
  placeholder,
  autoComplete,
  autoFocus,
  minLength,
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  minLength?: number;
}) {
  const [show, setShow] = useState(false);
  return (
    <label className="block">
      {label && <span className="mb-1.5 block text-xs font-medium text-ef-textDim">{label}</span>}
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          minLength={minLength}
          className="ef-input pr-11"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          // The button was sized to its 15px icon, i.e. a 15x15 tap target
          // sitting inside a form field -- unusable on a phone and below the
          // WCAG 2.5.8 minimum. The icon stays 15px; the hit area does not.
          className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-[var(--radius-sm)] text-ef-textMuted hover:text-ef-text"
          aria-label={show ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          <IcoEye size={15} />
        </button>
      </div>
    </label>
  );
}
